import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import { toast } from 'sonner';
import { Plus, Trash2, Download, Save, Loader2, TrendingUp, TrendingDown, DollarSign } from 'lucide-react';
import { accountingApi } from '../lib/api';

const fmt$ = (n) => {
  const v = Number(n || 0);
  const abs = Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return v < 0 ? `-$${abs}` : `$${abs}`;
};

const emptyRow = () => ({ label: '', amount: '', date: '', notes: '' });

/**
 * Per-unit Custom Settlement dialog.  Lets a dispatcher build a one-off
 * settlement for a custom date window, layering manual earnings and
 * deductions on top of the loads + deductions already in the system,
 * then download it as a branded PDF matching the Weekly Settlement.
 */
export default function CustomSettlementDialog({ open, onOpenChange, unit }) {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [earnings, setEarnings] = useState([]);
  const [deductions, setDeductions] = useState([]);
  const [notes, setNotes] = useState('');
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState(null);

  useEffect(() => {
    if (open) {
      // Default: current month
      const now = new Date();
      const first = new Date(now.getFullYear(), now.getMonth(), 1);
      const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      setStartDate(iso(first));
      setEndDate(iso(last));
      setEarnings([]);
      setDeductions([]);
      setNotes('');
      setSavedId(null);
      setTotals(null);
    }
  }, [open, unit?.unit_id]);

  const refreshPreview = useCallback(async () => {
    if (!unit?.unit_id || !startDate || !endDate) return;
    setLoading(true);
    try {
      const payload = {
        unit_id: unit.unit_id,
        start_date: startDate,
        end_date: endDate,
        manual_earnings: earnings
          .filter((e) => e.label && e.amount !== '')
          .map((e) => ({ label: e.label, amount: parseFloat(e.amount) || 0, date: e.date || null, notes: e.notes || null })),
        manual_deductions: deductions
          .filter((d) => d.label && d.amount !== '')
          .map((d) => ({ label: d.label, amount: parseFloat(d.amount) || 0, date: d.date || null, notes: d.notes || null })),
      };
      const res = await accountingApi.previewCustomSettlement(payload);
      setTotals(res.data);
    } catch (e) {
      // Non-fatal: totals stale but the user can still fill in
    } finally {
      setLoading(false);
    }
  }, [unit?.unit_id, startDate, endDate, earnings, deductions]);

  useEffect(() => {
    // Debounced auto-preview whenever inputs change
    const h = setTimeout(() => { refreshPreview(); }, 400);
    return () => clearTimeout(h);
  }, [refreshPreview]);

  const addEarning = () => setEarnings((p) => [...p, emptyRow()]);
  const addDeduction = () => setDeductions((p) => [...p, emptyRow()]);
  const removeEarning = (i) => setEarnings((p) => p.filter((_, idx) => idx !== i));
  const removeDeduction = (i) => setDeductions((p) => p.filter((_, idx) => idx !== i));
  const patchEarning = (i, patch) => setEarnings((p) => p.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const patchDeduction = (i, patch) => setDeductions((p) => p.map((r, idx) => idx === i ? { ...r, ...patch } : r));

  const save = async () => {
    if (!unit?.unit_id) return;
    setSaving(true);
    try {
      const payload = {
        unit_id: unit.unit_id,
        start_date: startDate,
        end_date: endDate,
        manual_earnings: earnings
          .filter((e) => e.label && e.amount !== '')
          .map((e) => ({ label: e.label, amount: parseFloat(e.amount) || 0, date: e.date || null, notes: e.notes || null })),
        manual_deductions: deductions
          .filter((d) => d.label && d.amount !== '')
          .map((d) => ({ label: d.label, amount: parseFloat(d.amount) || 0, date: d.date || null, notes: d.notes || null })),
        notes: notes || null,
      };
      const res = await accountingApi.createCustomSettlement(payload);
      setSavedId(res.data.id);
      toast.success('Custom settlement saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const downloadPdf = async () => {
    let id = savedId;
    if (!id) {
      // Save first, then download
      await save();
      // Grab id from state — save() has already updated it, but state is async.
      // Fallback: re-list and take newest.
      try {
        const list = await accountingApi.listCustomSettlements(unit.unit_id);
        id = list.data.settlements?.[0]?.id;
      } catch { /* ignore */ }
    }
    if (!id) return;
    try {
      const res = await accountingApi.customSettlementPdf(id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `custom-settlement-${unit.unit_number || 'unit'}-${startDate}-to-${endDate}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch { toast.error('PDF export failed'); }
  };

  const gross = (totals?.gross_payout || 0) + (totals?.manual_earning_total || 0);
  const totalDed = (totals?.existing_deduction_total || 0) + (totals?.manual_deduction_total || 0);
  const net = totals?.net ?? 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[900px] w-[92vw] max-h-[90vh] flex flex-col p-0" data-testid="custom-settlement-dialog">
        <DialogHeader className="p-4 pb-2 border-b">
          <DialogTitle className="flex items-center gap-2">
            <DollarSign className="h-4 w-4" /> Custom Settlement · Unit #{unit?.unit_number}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Build a one-off settlement for a custom date window with your own manual earnings and deductions on top of what{"'"}s already in the system.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4">
          {/* Date range */}
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Start date</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                     className="h-8 text-sm" data-testid="cs-start-date" />
            </div>
            <div>
              <Label className="text-xs">End date</Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                     className="h-8 text-sm" data-testid="cs-end-date" />
            </div>
          </div>

          {/* Loaded existing snapshot */}
          <Card>
            <CardContent className="p-3">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-2">Loaded from the system</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
                <div><div className="text-[10px] text-muted-foreground">Loads</div><div className="font-medium">{totals?.loads?.length ?? 0}</div></div>
                <div><div className="text-[10px] text-muted-foreground">Gross payout</div><div className="font-medium tabular-nums">{fmt$(totals?.gross_payout || 0)}</div></div>
                <div><div className="text-[10px] text-muted-foreground">Dispatch fee</div><div className="font-medium tabular-nums">{fmt$(totals?.dispatch_fees || 0)}</div></div>
                <div><div className="text-[10px] text-muted-foreground">Existing deductions</div><div className="font-medium tabular-nums text-red-700">{fmt$(totals?.existing_deduction_total || 0)}</div></div>
              </div>
            </CardContent>
          </Card>

          {/* Manual Earnings */}
          <LineItemsSection
            title="Manual Earnings"
            testIdPrefix="cs-earn"
            positive
            rows={earnings}
            onAdd={addEarning}
            onRemove={removeEarning}
            onPatch={patchEarning}
          />

          {/* Manual Deductions */}
          <LineItemsSection
            title="Manual Deductions"
            testIdPrefix="cs-ded"
            positive={false}
            rows={deductions}
            onAdd={addDeduction}
            onRemove={removeDeduction}
            onPatch={patchDeduction}
          />

          {/* Notes */}
          <div>
            <Label className="text-xs">Notes (optional)</Label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)}
                   placeholder="Any context for this settlement…"
                   className="h-8 text-sm" data-testid="cs-notes" />
          </div>

          {/* Totals preview */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <MetricCard label="Gross" value={gross} color="emerald" testId="cs-gross" />
            <MetricCard label="Dispatch Fee" value={totals?.dispatch_fees || 0} color="blue" testId="cs-dispatch" />
            <MetricCard label="Deductions" value={totalDed} color="red" testId="cs-deductions" />
            <MetricCard label="Net Payable" value={net} color={net >= 0 ? 'emerald' : 'red'} big testId="cs-net" />
          </div>
        </div>

        <DialogFooter className="p-3 border-t flex-wrap gap-2">
          <div className="text-[10px] text-muted-foreground mr-auto">
            {loading && <span className="flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Recalculating…</span>}
            {savedId && !loading && <span className="text-emerald-700">✓ Saved</span>}
          </div>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="cs-close">Close</Button>
          <Button onClick={save} disabled={saving} data-testid="cs-save"
                  className="bg-blue-600 hover:bg-blue-700 text-white">
            {saving ? <Loader2 className="h-3 w-3 animate-spin mr-1.5" /> : <Save className="h-3 w-3 mr-1.5" />}
            {savedId ? 'Save as new' : 'Save Settlement'}
          </Button>
          <Button onClick={downloadPdf} data-testid="cs-download-pdf"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white">
            <Download className="h-3 w-3 mr-1.5" /> Save &amp; Download PDF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LineItemsSection({ title, testIdPrefix, positive, rows, onAdd, onRemove, onPatch }) {
  const Icon = positive ? TrendingUp : TrendingDown;
  const color = positive ? 'text-emerald-700' : 'text-red-700';
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className={`text-xs font-medium flex items-center gap-1.5 ${color}`}>
          <Icon className="h-3.5 w-3.5" /> {title}
        </div>
        <Button size="sm" variant="outline" onClick={onAdd} className="h-7 text-xs" data-testid={`${testIdPrefix}-add`}>
          <Plus className="h-3 w-3 mr-1" /> Add line
        </Button>
      </div>
      {rows.length === 0 ? (
        <div className="text-[11px] text-muted-foreground italic py-2 border rounded text-center bg-slate-50 dark:bg-slate-900/40">
          No manual {positive ? 'earnings' : 'deductions'} yet. Click {'"'}Add line{'"'} to add one.
        </div>
      ) : (
        <div className="space-y-1.5">
          {rows.map((r, i) => (
            <div key={i} className="grid grid-cols-[1fr_100px_100px_28px] gap-1.5" data-testid={`${testIdPrefix}-row-${i}`}>
              <Input placeholder="Description (e.g., Fuel bonus, Toll refund)" value={r.label}
                     onChange={(e) => onPatch(i, { label: e.target.value })}
                     className="h-7 text-xs" data-testid={`${testIdPrefix}-label-${i}`} />
              <Input placeholder="Amount" type="number" step="0.01" value={r.amount}
                     onChange={(e) => onPatch(i, { amount: e.target.value })}
                     className="h-7 text-xs text-right" data-testid={`${testIdPrefix}-amount-${i}`} />
              <Input placeholder="Date" type="date" value={r.date}
                     onChange={(e) => onPatch(i, { date: e.target.value })}
                     className="h-7 text-xs" data-testid={`${testIdPrefix}-date-${i}`} />
              <Button size="icon" variant="ghost" onClick={() => onRemove(i)}
                      className="h-7 w-7 text-red-600" data-testid={`${testIdPrefix}-remove-${i}`}>
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MetricCard({ label, value, color, big, testId }) {
  const cls = {
    emerald: 'text-emerald-700 bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200',
    red: 'text-red-700 bg-red-50 dark:bg-red-950/30 border-red-200',
    blue: 'text-blue-700 bg-blue-50 dark:bg-blue-950/30 border-blue-200',
  }[color] || 'text-slate-700 bg-slate-50 border-slate-200';
  return (
    <div className={`border rounded p-2 ${cls}`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wide opacity-70">{label}</div>
      <div className={`font-bold tabular-nums ${big ? 'text-xl' : 'text-sm'}`}>{fmt$(value)}</div>
    </div>
  );
}
